/**
 * `handoff_config` renderer — invisible auto-advancing step that configures
 * the redirect URL for the terminal handoff.
 *
 * The step evaluates a list of URL options, each with an optional `condition`
 * that checks against the current answers bag. The first matching option's
 * URL is written to `answers.__handoff_url`, which `useHandoffRedirect`
 * reads at redirect time. If no condition matches, `defaultUrl` is used.
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "handoff_config",
 *     "advances_progress": false,
 *     "data": {
 *       "answerKey": "__handoff_url",
 *       "defaultUrl": "https://app.spicychat.ai/subscribe",
 *       "options": [
 *         {
 *           "url": "https://companio.chat/subscribe?plan=premium",
 *           "condition": {
 *             "answerKey": "preferred_plan",
 *             "operator": "eq",
 *             "value": "premium"
 *           }
 *         },
 *         {
 *           "url": "https://companio.chat/subscribe?plan=basic",
 *           "condition": {
 *             "answerKey": "preferred_plan",
 *             "operator": "eq",
 *             "value": "basic"
 *           }
 *         }
 *       ]
 *     }
 *   }
 *
 * Condition operators:
 *   - `eq`: exact equality (string or number)
 *   - `neq`: not equal
 *   - `contains`: value is included in an array answer
 *   - `exists`: answer key is present and non-empty
 */
import { useEffect, useRef } from "react";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

/** Well-known answer key used as the contract between this renderer and useHandoffRedirect. */
export const HANDOFF_URL_ANSWER_KEY = "__handoff_url";

const conditionSchema = z.object({
  answerKey: z.string().min(1),
  operator: z.enum(["eq", "neq", "contains", "exists"]),
  value: z.unknown().optional(),
});

const optionSchema = z.object({
  url: z.string().min(1),
  condition: conditionSchema.optional(),
});

export const handoffConfigSchema = z
  .object({
    answerKey: z.string().min(1),
    defaultUrl: z.string().min(1),
    options: z.array(optionSchema).optional(),
  })
  .passthrough();

export type HandoffConfigData = z.infer<typeof handoffConfigSchema>;

type Condition = z.infer<typeof conditionSchema>;

const evaluateCondition = (condition: Condition, answers: Record<string, unknown>): boolean => {
  const answer = answers[condition.answerKey];
  switch (condition.operator) {
    case "eq":
      return answer === condition.value;
    case "neq":
      return answer != null && answer !== condition.value;
    case "contains":
      return Array.isArray(answer) && answer.includes(condition.value);
    case "exists":
      return answer !== undefined && answer !== null && answer !== "";
    default:
      return false;
  }
};

const warnUnreachableOptions = (options: HandoffConfigData["options"]): void => {
  if (!options) return;
  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    if (opt && !opt.condition && i < options.length - 1) {
      // eslint-disable-next-line no-console -- intentional dev-time warning for funnel authors
      console.warn(
        `[handoff_config] Option at index ${i} has no condition and will always match, ` +
          `making ${options.length - 1 - i} subsequent option(s) unreachable. ` +
          `Move conditionless options to the end of the list.`,
      );
    }
  }
};

export type HandoffResolution = {
  readonly url: string;
  /** Index of the matching option, or `null` when `defaultUrl` was used. */
  readonly matchedOptionIndex: number | null;
};

export const resolveHandoff = (
  data: HandoffConfigData,
  answers: Record<string, unknown>,
): HandoffResolution => {
  if (data.options) {
    for (let i = 0; i < data.options.length; i++) {
      const option = data.options[i];
      if (option && (!option.condition || evaluateCondition(option.condition, answers))) {
        return { url: option.url, matchedOptionIndex: i };
      }
    }
  }
  return { url: data.defaultUrl, matchedOptionIndex: null };
};

const resolveHandoffUrl = (data: HandoffConfigData, answers: Record<string, unknown>): string => {
  warnUnreachableOptions(data.options);
  return resolveHandoff(data, answers).url;
};

const HandoffConfigRenderer = ({
  data,
  answers,
  onAnswer,
  onAdvance,
}: RendererProps<HandoffConfigData>) => {
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    const url = resolveHandoffUrl(data, answers);
    onAnswer(data.answerKey, { value: url });
    onAdvance();
  }, [data, answers, onAnswer, onAdvance]);

  return null;
};

export const handoffConfigRenderer = createRenderer({
  type: "handoff_config",
  dataSchema: handoffConfigSchema,
  label: "Handoff config",
  invisible: true,
  Component: HandoffConfigRenderer,
});

export default handoffConfigRenderer;
